import { Button, Modal, Select, Space, Table, Tooltip, Typography, message } from "antd";
import { Content } from "antd/lib/layout/layout";
import { useEffect, useMemo, useState } from "react";
import { getBrands } from "components/brands/brands.api";
import { getFraudRiskAccounts, setFraudRiskReview } from "./fraud_risk.api";
import {
    columnDef_accountId,
    columnDef_computedDate,
    columnDef_contact,
    columnDef_level,
    columnDef_reasons,
    columnDef_review,
    columnDef_score,
} from "./fraud_risk_columns";
import { SORT_DIRECTIONS } from "common/constants";
import { ScoreLegend } from "./ScoreLegend";

const { Text } = Typography;

// Lookup ids, fixed in db-brand-portal's static Lookup table.
const LEVEL = {
    NOT_ASSESSED: 1410,
    INSUFFICIENT_EVIDENCE: 1411,
    NONE: 1412,
    ELEVATED: 1413,
    HIGH: 1414,
};
const REVIEW = { CLEARED: 1419, CONFIRMED: 1420 };
const NOT_REVIEWED = -1;

const LEVEL_OPTIONS = [
    { value: null, label: "All statuses" },
    { value: LEVEL.HIGH, label: "High" },
    { value: LEVEL.ELEVATED, label: "Elevated" },
    { value: LEVEL.NONE, label: "None" },
    { value: LEVEL.INSUFFICIENT_EVIDENCE, label: "Insufficient evidence" },
    { value: LEVEL.NOT_ASSESSED, label: "Not assessed" },
];

// What each verdict actually does. Shown both on hover and in the confirm dialog, so the two can
// never drift apart.
//
// Deliberately does not promise that a cleared contact comes back to this queue: the clearance
// expiry returns them to SCORING, but AccountRiskAssessment_UpsertAsJson keeps ReviewOutcomeId,
// so the digest and the default "not yet reviewed" view still exclude them.
const REVIEW_HELP = {
    clear:
        "Records that a person looked and found nothing. Drops the contact out of this queue and " +
        "out of the brand's digest, and stops them being re-scored for 180 days.",
    confirm:
        "Records a confirmed finding. The contact is never re-scored or listed again. No payment " +
        "is withheld — that stays the brand's decision.",
    reopen:
        "Removes the recorded verdict and its reviewer, returning the contact to scoring on the " +
        "next nightly run.",
};

const reviewHelpFor = (outcomeId) => {
    if (outcomeId === REVIEW.CONFIRMED) return REVIEW_HELP.confirm;
    if (outcomeId === REVIEW.CLEARED) return REVIEW_HELP.clear;
    return REVIEW_HELP.reopen;
};

const REVIEW_OPTIONS = [
    { value: null, label: "Any" },
    { value: NOT_REVIEWED, label: "Not yet reviewed" },
    { value: REVIEW.CLEARED, label: "Cleared" },
    { value: REVIEW.CONFIRMED, label: "Confirmed" },
];

export const FraudRisk = () => {
    // Static Modal/message rather than App.useApp(): this app mounts ConfigProvider but not antd's
    // <App> provider, so the hook API is unavailable. Matches Blacklist.js.
    const [brands, setBrands] = useState([]);
    const [brandsLoading, setBrandsLoading] = useState(false);
    const [brandUserId, setBrandUserId] = useState(null);
    const [levelId, setLevelId] = useState(null);
    const [reviewOutcomeId, setReviewOutcomeId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setBrandsLoading(true);
        getBrands()
            .then((data) => setBrands(data ?? []))
            .finally(() => setBrandsLoading(false));
    }, []);

    const fetchRows = (brand = brandUserId, level = levelId, review = reviewOutcomeId) => {
        // Brand-scoped: the brand is what resolves which shard to read.
        if (!brand) {
            setRows([]);
            return;
        }
        setLoading(true);
        getFraudRiskAccounts(brand, {
            levelId: level ?? undefined,
            reviewOutcomeId: review ?? undefined,
        })
            .then((result) => setRows(result ?? []))
            .finally(() => setLoading(false));
    };

    const onReview = (record, outcomeId, label) => {
        Modal.confirm({
            title: `Mark ${record?.displayName} as ${label}?`,
            content: reviewHelpFor(outcomeId),
            okText: label,
            onOk: () =>
                setFraudRiskReview(record?.accountId, record?.connectionUserId, outcomeId).then(
                    (response) => {
                        if (response?.success) {
                            message.success("Review recorded.");
                            fetchRows();
                        } else {
                            message.error(response?.message ?? "Failed to record review.");
                        }
                    },
                ),
        });
    };

    const brandOptions = useMemo(
        () =>
            brands
                .filter((b) => b?.role === "Owner")
                .sort((a, b) =>
                    (a?.businessName ?? "").localeCompare(b?.businessName ?? ""),
                )
                .map((b) => ({
                    value: b.userId,
                    label: b.businessName ?? b.fullName ?? String(b.userId),
                })),
        [brands],
    );

    const columns = useMemo(
        () => [
            columnDef_contact,
            columnDef_accountId,
            columnDef_level,
            columnDef_score,
            columnDef_reasons,
            columnDef_review,
            columnDef_computedDate,
            {
                title: "",
                key: "actions",
                width: "190px",
                render: (_, record) =>
                    record?.reviewOutcomeId ? (
                        <Tooltip title={REVIEW_HELP.reopen}>
                            <Button size="small" onClick={() => onReview(record, null, "Reopened")}>
                                Reopen
                            </Button>
                        </Tooltip>
                    ) : (
                        <Space size={4}>
                            <Tooltip title={REVIEW_HELP.clear}>
                                <Button
                                    size="small"
                                    onClick={() => onReview(record, REVIEW.CLEARED, "Cleared")}
                                >
                                    Clear
                                </Button>
                            </Tooltip>
                            <Tooltip title={REVIEW_HELP.confirm}>
                                <Button
                                    size="small"
                                    danger
                                    onClick={() => onReview(record, REVIEW.CONFIRMED, "Confirmed")}
                                >
                                    Confirm
                                </Button>
                            </Tooltip>
                        </Space>
                    ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [brandUserId, levelId, reviewOutcomeId],
    );

    return (
        // The app shell is fixed to the viewport with overflow hidden, so a page scrolls only if
        // it scrolls itself. Needed once the legend is expanded and pushes the table down;
        // minHeight: 0 lets this flex child shrink to the viewport instead of growing past it.
        <Content style={{ padding: 24, overflowY: "auto", minHeight: 0 }}>
            <ScoreLegend />

            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    style={{ width: 280 }}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    loading={brandsLoading}
                    options={brandOptions}
                    placeholder="Select a brand"
                    value={brandUserId}
                    onChange={(value) => {
                        const id = value ?? null;
                        setBrandUserId(id);
                        fetchRows(id);
                    }}
                />
                <Select
                    style={{ width: 200 }}
                    options={LEVEL_OPTIONS}
                    value={levelId}
                    onChange={(value) => {
                        setLevelId(value ?? null);
                        fetchRows(brandUserId, value ?? null);
                    }}
                />
                <Select
                    style={{ width: 180 }}
                    options={REVIEW_OPTIONS}
                    value={reviewOutcomeId}
                    onChange={(value) => {
                        setReviewOutcomeId(value ?? null);
                        fetchRows(brandUserId, levelId, value ?? null);
                    }}
                />
            </Space>

            <Table
                rowKey={(record) => `${record.connectionUserId}-${record.accountId}`}
                columns={columns}
                dataSource={rows}
                loading={loading}
                sortDirections={SORT_DIRECTIONS}
                pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                locale={{
                    emptyText: brandUserId
                        ? "No contacts match this filter."
                        : "Select a brand to begin.",
                }}
                scroll={{ y: "calc(100vh - 320px)", x: 1300 }}
                style={{ width: "100%" }}
            />

            {brandUserId ? (
                <Text type="secondary" style={{ display: "block", marginTop: 16 }}>
                    Showing what the nightly scorer has assessed for this brand. Contacts with no
                    score could not be evaluated — that is not the same as clean.
                </Text>
            ) : null}
        </Content>
    );
};
